import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ProfilePage from "@/pages/ProfilePage";
import AchievementsPage from "@/pages/AchievementsPage";
import VerificationPage from "@/pages/VerificationPage";
import AdminDashboard from "@/pages/AdminDashboard";
import GuidePage from "@/pages/GuidePage";
import DocumentsPage from "@/pages/DocumentsPage";
import EmbedStats from "@/pages/EmbedStats";
import IntegrationPage from "@/pages/IntegrationPage";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#B85C38]" />
    </div>
  );
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "pramuka" ? "/profil" : "/dashboard"} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={user.role === "pramuka" ? "/profil" : "/dashboard"} replace />;
  return children;
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.role === "pramuka" ? "/profil" : "/dashboard", { replace: true });
      })
      .catch(() => {
        setUser(false);
        window.history.replaceState(null, "", "/auth");
        navigate("/auth", { replace: true });
      });
  }, [location.hash, navigate, setUser]);
  return <Loading />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
      <Route path="/petunjuk" element={<GuidePage />} />
      <Route path="/embed/statistik" element={<EmbedStats />} />
      <Route path="/integrasi" element={<Protected roles={["admin"]}><IntegrationPage /></Protected>} />
      <Route path="/dokumen" element={<Protected roles={["admin"]}><DocumentsPage /></Protected>} />
      <Route path="/profil" element={<Protected roles={["pramuka"]}><ProfilePage /></Protected>} />
      <Route path="/prestasi" element={<Protected roles={["pramuka"]}><AchievementsPage /></Protected>} />
      <Route path="/dashboard" element={<Protected roles={["admin", "pembina"]}><AdminDashboard /></Protected>} />
      <Route path="/verifikasi" element={<Protected roles={["admin", "pembina"]}><VerificationPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
